import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LazyImage from '../components/LazyImage';
import { projectService } from '../services/projectService';
import useLanguage from '../hooks/useLanguage';
import { getLocalizedProject } from '../utils/multilingual';
import { safeRender } from '../utils/safeRender';
import { optimizeGalleryImage } from '../utils/imageOptimization';
import './ProjectDetailPage.css';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const { language, changeLanguage } = useLanguage();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [imageLoadError, setImageLoadError] = useState({});

  // Create localized project that updates when language changes
  const localizedProject = React.useMemo(() => {
    return project ? getLocalizedProject(project, language) : null;
  }, [project, language]);

  useEffect(() => {
    loadProject();
  }, [id]);

  useEffect(() => {
    if (project) {
      loadRelatedProjects();
    }
  }, [project]);

  const loadProject = async () => {
    setLoading(true);
    try {
      const response = await projectService.getProjectById(id);
      setProject(response.data);
    } catch (err) {
      setError('Projekat nije pronađen');
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedProjects = async () => {
    try {
      // Ensure category is a string, not an object
      const categoryString = typeof project.category === 'string'
        ? project.category
        : project.category?.name || project.category?.title || String(project.category);

      const response = await projectService.getProjectsByCategory(categoryString, {
        limit: 4,
        page: 1
      });

      // Filter out current project from related projects
      const filtered = response.data.projects.filter(p => p._id !== project._id);
      setRelatedProjects(filtered.slice(0, 3));
    } catch (err) {
      console.error('Error loading related projects:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);

    if (language === 'SR') {
      // Custom formatting for Serbian with capitalized month
      const months = [
        'Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun',
        'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'
      ];

      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      return `${day}. ${month} ${year}`;
    }

    const localeMap = {
      EN: 'en-US',
      DE: 'de-DE'
    };

    return date.toLocaleDateString(localeMap[language] || 'sr-RS', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleImageClick = useCallback((index) => {
    if (project?.gallery && index >= 0 && index < project.gallery.length) {
      setSelectedImageIndex(index);
    }
  }, [project?.gallery]);

  const handlePrevImage = useCallback(() => {
    if (!project?.gallery?.length) return;
    setSelectedImageIndex(prev =>
      prev === 0 ? project.gallery.length - 1 : prev - 1
    );
  }, [project?.gallery?.length]);

  const handleNextImage = useCallback(() => {
    if (!project?.gallery?.length) return;
    setSelectedImageIndex(prev =>
      prev === project.gallery.length - 1 ? 0 : prev + 1
    );
  }, [project?.gallery?.length]);


  const handleKeyDown = useCallback((e) => {
    if (!project?.gallery?.length) return;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePrevImage();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleNextImage();
    } else if (e.key === 'Escape') {
      // Close lightbox if implemented
      e.preventDefault();
    }
  }, [project?.gallery?.length, handlePrevImage, handleNextImage]);

  useEffect(() => {
    if (project?.gallery?.length > 0) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [project?.gallery?.length, handleKeyDown]);

  // Ensure selectedImageIndex is valid and reset when project changes
  useEffect(() => {
    if (project?.gallery?.length > 0) {
      if (selectedImageIndex >= project.gallery.length || selectedImageIndex < 0) {
        setSelectedImageIndex(0);
      }
    } else {
      setSelectedImageIndex(0);
    }
  }, [project?.gallery?.length, selectedImageIndex]);

  // Reset selectedImageIndex when project changes
  useEffect(() => {
    setSelectedImageIndex(0);
    setImageLoadError({});
  }, [project?._id]);

  // Handle image loading errors
  const handleImageError = useCallback((imageIndex) => {
    setImageLoadError(prev => ({
      ...prev,
      [imageIndex]: true
    }));
  }, []);

  const handleImageLoad = useCallback((imageIndex) => {
    setImageLoadError(prev => ({
      ...prev,
      [imageIndex]: false
    }));
  }, []);

  const content = {
    SR: {
      nav: {
        home: 'Početna',
        products: 'Proizvodi',
        services: 'Usluge',
        projects: 'Projekti',
        about: 'O nama',
        ecology: 'Ekologija',
        contact: 'Kontakt'
      },
      loading: {
        project: 'Učitavanje projekta...'
      },
      error: {
        title: 'Greška pri učitavanju',
        retry: 'Pokušaj ponovo',
        backToProjects: 'Nazad na projekte'
      },
      breadcrumb: {
        home: 'Početna',
        projects: 'Projekti'
      },
      project: {
        featured: 'Izdvojeno',
        client: 'Klijent:',
        location: 'Lokacija:',
        completionDate: 'Datum završetka:',
        about: 'O projektu',
        keywords: 'Ključne reči',
        relatedProjects: 'Slični projekti',
        backToAllProjects: 'Nazad na sve projekte'
      },
      gallery: {
        prevImage: 'Prethodna slika',
        nextImage: 'Sledeća slika',
        of: 'od',
        thumbnail: 'Thumbnail'
      },
      footer: {
        contact: {
          title: 'Kontakt informacije',
          address: 'Gandijeva 235/13, 11073 Beograd',
          phone: '+381 62 213 492',
          email: 'constructions@mtc.co.rs',
          workingHours: 'Ponedeljak-Petak: 08:00-16:00'
        },
        quickLinks: {
          title: 'Brzi linkovi',
          products: 'Proizvodi',
          services: 'Usluge',
          projects: 'Projekti',
          contact: 'Kontakt'
        },
        social: {
          title: 'Pratite nas',
          facebook: 'Facebook',
          instagram: 'Instagram',
          linkedin: 'LinkedIn'
        },
        certificates: {
          title: 'Sertifikati i partneri'
        }
      }
    },
    EN: {
      nav: {
        home: 'Home',
        products: 'Products',
        services: 'Services',
        projects: 'Projects',
        about: 'About',
        ecology: 'Ecology',
        contact: 'Contact'
      },
      loading: {
        project: 'Loading project...'
      },
      error: {
        title: 'Error loading',
        retry: 'Try again',
        backToProjects: 'Back to projects'
      },
      breadcrumb: {
        home: 'Home',
        projects: 'Projects'
      },
      project: {
        featured: 'Featured',
        client: 'Client:',
        location: 'Location:',
        completionDate: 'Completion date:',
        about: 'About project',
        keywords: 'Keywords',
        relatedProjects: 'Related projects',
        backToAllProjects: 'Back to all projects'
      },
      gallery: {
        prevImage: 'Previous image',
        nextImage: 'Next image',
        of: 'of',
        thumbnail: 'Thumbnail'
      },
      footer: {
        contact: {
          title: 'Contact Information',
          address: 'Gandijeva 235/13, 11073 Belgrade',
          phone: '+381 62 213 492',
          email: 'constructions@mtc.co.rs',
          workingHours: 'Monday-Friday: 08:00-16:00'
        },
        quickLinks: {
          title: 'Quick Links',
          products: 'Products',
          services: 'Services',
          projects: 'Projects',
          contact: 'Contact'
        },
        social: {
          title: 'Follow Us',
          facebook: 'Facebook',
          instagram: 'Instagram',
          linkedin: 'LinkedIn'
        },
        certificates: {
          title: 'Certificates & Partners'
        }
      }
    },
    DE: {
      nav: {
        home: 'Startseite',
        products: 'Produkte',
        services: 'Dienstleistungen',
        projects: 'Projekte',
        about: 'Über uns',
        ecology: 'Ökologie',
        contact: 'Kontakt'
      },
      loading: {
        project: 'Projekt wird geladen...'
      },
      error: {
        title: 'Fehler beim Laden',
        retry: 'Erneut versuchen',
        backToProjects: 'Zurück zu Projekten'
      },
      breadcrumb: {
        home: 'Startseite',
        projects: 'Projekte'
      },
      project: {
        featured: 'Hervorgehoben',
        client: 'Kunde:',
        location: 'Standort:',
        completionDate: 'Fertigstellungsdatum:',
        about: 'Über das Projekt',
        keywords: 'Schlüsselwörter',
        relatedProjects: 'Ähnliche Projekte',
        backToAllProjects: 'Zurück zu allen Projekten'
      },
      gallery: {
        prevImage: 'Vorheriges Bild',
        nextImage: 'Nächstes Bild',
        of: 'von',
        thumbnail: 'Vorschaubild'
      },
      footer: {
        contact: {
          title: 'Kontaktinformationen',
          address: 'Gandijeva 235/13, 11073 Belgrad',
          phone: '+381 62 213 492',
          email: 'constructions@mtc.co.rs',
          workingHours: 'Montag-Freitag: 08:00-16:00'
        },
        quickLinks: {
          title: 'Schnelllinks',
          products: 'Produkte',
          services: 'Dienstleistungen',
          projects: 'Projekte',
          contact: 'Kontakt'
        },
        social: {
          title: 'Folgen Sie uns',
          facebook: 'Facebook',
          instagram: 'Instagram',
          linkedin: 'LinkedIn'
        },
        certificates: {
          title: 'Zertifikate & Partner'
        }
      }
    }
  };

  const currentContent = content[language];

  if (loading) {
    return (
      <div className="project-detail-page">
        <Header 
          language={language} 
          onLanguageChange={changeLanguage} 
          content={currentContent} 
        />
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{currentContent.loading.project}</p>
          </div>
        </div>
        <Footer content={currentContent} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-detail-page">
        <Header 
          language={language} 
          onLanguageChange={changeLanguage} 
          content={currentContent} 
        />
        <div className="container">
          <div className="error-state">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
              <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <h2>{currentContent.error.title}</h2>
            <p>{error}</p>
            <div className="error-actions">
              <button onClick={loadProject} className="retry-btn">{currentContent.error.retry}</button>
              <Link to="/projekti" className="back-btn">{currentContent.error.backToProjects}</Link>
            </div>
          </div>
        </div>
        <Footer content={currentContent} />
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="project-detail-page">
      <Header 
        language={language} 
        onLanguageChange={changeLanguage} 
        content={currentContent} 
      />
      
      {/* Breadcrumb */}
      <div className="breadcrumb-section">
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/">{currentContent.breadcrumb.home}</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/projekti">{currentContent.breadcrumb.projects}</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{safeRender(localizedProject?.localizedTitle, language)}</span>
          </nav>
        </div>
      </div>

      <div className="container">
        <div className="project-detail-content">
          {/* Project Header */}
          <header className="project-header">
            <div className="project-meta">
              <span className="project-category">{safeRender(localizedProject?.localizedCategory, language)}</span>
              {project.featured && (
                <span className="featured-badge">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"
                      fill="currentColor"
                    />
                  </svg>
                  {currentContent.project.featured}
                </span>
              )}
            </div>
            <h1 className="project-title">{safeRender(localizedProject?.localizedTitle, language)}</h1>
            <div className="project-details">
              {(() => {
                const clientText = safeRender(localizedProject?.localizedClient, language);
                return clientText && !clientText.includes('Missing') && !clientText.includes('translation') ? (
                  <div className="detail-item">
                    <span className="detail-label">{currentContent.project.client}</span>
                    <span className="detail-value">{clientText}</span>
                  </div>
                ) : null;
              })()}

              {(() => {
                const locationText = safeRender(localizedProject?.localizedLocation, language);
                return locationText && !locationText.includes('Missing') && !locationText.includes('translation') ? (
                  <div className="detail-item">
                    <span className="detail-label">{currentContent.project.location}</span>
                    <span className="detail-value">{locationText}</span>
                  </div>
                ) : null;
              })()}

              {project.completionDate && (
                <div className="detail-item">
                  <span className="detail-label">{currentContent.project.completionDate}</span>
                  <span className="detail-value">{formatDate(project.completionDate)}</span>
                </div>
              )}
            </div>
          </header>

          {/* Image Gallery - Completely rebuilt */}
          {project.gallery && project.gallery.length > 0 && (
            <section className="project-gallery">
              <div className="gallery-container">

                {/* Main image display */}
                <div className="main-image-container">
                  <div className="image-wrapper">
                    <img
                      src={project.gallery[selectedImageIndex]?.url}
                      alt={project.gallery[selectedImageIndex]?.alt || `${safeRender(localizedProject?.localizedTitle, language)} ${selectedImageIndex + 1}`}
                      className="main-gallery-image"
                      onError={(e) => {
                        console.error('Error loading image:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>

                  {/* Navigation arrows */}
                  {project.gallery.length > 1 && (
                    <>
                      <button
                        onClick={handlePrevImage}
                        className="gallery-nav-btn prev-btn"
                        aria-label={currentContent.gallery.prevImage}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="15,18 9,12 15,6"></polyline>
                        </svg>
                      </button>

                      <button
                        onClick={handleNextImage}
                        className="gallery-nav-btn next-btn"
                        aria-label={currentContent.gallery.nextImage}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9,6 15,12 9,18"></polyline>
                        </svg>
                      </button>
                    </>
                  )}

                  {/* Image counter */}
                  {project.gallery.length > 1 && (
                    <div className="gallery-counter">
                      {selectedImageIndex + 1} {currentContent.gallery.of} {project.gallery.length}
                    </div>
                  )}
                </div>

                {/* Thumbnail strip */}
                {project.gallery.length > 1 && (
                  <div className="thumbnails-container">
                    <div className="thumbnails-strip">
                      {project.gallery.map((image, index) => (
                        <button
                          key={`thumb-${index}`}
                          onClick={() => handleImageClick(index)}
                          className={`thumbnail-btn ${index === selectedImageIndex ? 'active' : ''}`}
                        >
                          <img
                            src={image.url}
                            alt={`${currentContent.gallery.thumbnail} ${index + 1}`}
                            className="thumbnail-image"
                            onError={(e) => {
                              e.target.style.opacity = '0.3';
                            }}
                          />
                          <div className="thumbnail-overlay">
                            {index + 1}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </section>
          )}

          {/* Project Description */}
          {(() => {
            const descriptionText = safeRender(localizedProject?.localizedDescription, language);
            return descriptionText && !descriptionText.includes('Missing') && !descriptionText.includes('translation') ? (
              <section className="project-description">
                <h2>{currentContent.project.about}</h2>
                <div className="description-content">
                  {descriptionText.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ) : null;
          })()}

          {/* Project Tags */}
          {project.tags && project.tags.length > 0 && (
            <section className="project-tags">
              <h3>{currentContent.project.keywords}</h3>
              <div className="tags-list">
                {project.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
            </section>
          )}

          {/* Related Projects */}
          {relatedProjects.length > 0 && (
            <section className="related-projects">
              <h2>{currentContent.project.relatedProjects}</h2>
              <div className="related-projects-grid">
                {relatedProjects.map(relatedProject => (
                  <Link
                    key={relatedProject._id}
                    to={`/projekti/${relatedProject._id}`}
                    className="related-project-card"
                  >
                    <div className="related-project-image">
                      <LazyImage
                        src={relatedProject.gallery?.[0]?.url || '/images/placeholder/project-placeholder.jpg'}
                        alt={relatedProject.title}
                        width={400}
                        height={250}
                        quality="auto:good"
                        placeholder={true}
                      />
                    </div>
                    <div className="related-project-content">
                      <h3>{relatedProject.title}</h3>
                      <p className="related-project-category">{relatedProject.category}</p>
                      {relatedProject.completionDate && (
                        <p className="related-project-date">
                          {formatDate(relatedProject.completionDate)}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Navigation */}
          <section className="project-navigation">
            <Link to="/projekti" className="back-to-projects">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {currentContent.project.backToAllProjects}
            </Link>
          </section>
        </div>
      </div>
      
      <Footer content={currentContent} />
    </div>
  );
};

export default ProjectDetailPage;